import { Component, EventEmitter, Input, OnInit, Output, ViewChild, OnChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { SelectItem, SelectItemGroup, ConfirmationService, MenuItem } from 'primeng/api';
import { ParticipantData } from 'projects/admin/src/app/orders/partials/participant-form/participant-form.component';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { OrderHelperService } from 'projects/admin/src/app/services/order-helper.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { DetailModes } from 'projects/admin/src/app/shared/constants';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { CANCELLATION_REASONS, UPLOAD_MODE_MULTI } from 'projects/core/src/lib/constants';
import { AcModel } from 'projects/core/src/lib/models/ac.model';
import { ResourceModel } from 'projects/core/src/lib/models/resource.model';
import { TemplateModel } from 'projects/core/src/lib/models/template.model';
import { of, switchMap, forkJoin } from 'rxjs';
import { OrderDetailCandidatesComponent } from '../order-detail-candidates/order-detail-candidates.component';
import { OrderDetailDocumentsComponent } from '../order-detail-documents/order-detail-documents.component';
import { OrderDetailJobinformationComponent } from '../order-detail-jobinformation/order-detail-jobinformation.component';
import { OrderDetailOrderdataComponent } from '../order-detail-orderdata/order-detail-orderdata.component';
import { OrderDetailParticipantsComponent } from '../order-detail-participants/order-detail-participants.component';
import { BusinessunitModel } from 'projects/core/src/lib/models/businessunit.model';

@Component({
  selector: 'app-order-detail-basedata',
  templateUrl: './order-detail-basedata.component.html',
  styleUrls: ['./order-detail-basedata.component.scss']
})
export class OrderDetailBasedataComponent implements OnInit, OnChanges {

  @Input() order: AcModel = null;
  @Input() mode: DetailModes | null = null;
  @Input() options: { [key: string]: Array<SelectItem | SelectItemGroup> } = {};
  @Input() templates: Array<TemplateModel> = [];
  @Input() texts: { [key: string]: string } = {};
  @Input() user: ParticipantData = null;
  @Input() backRoute: string;

  @Output() orderChange = new EventEmitter<AcModel>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() setEdit = new EventEmitter<string>();
  @Output() setClearingUrl = new EventEmitter<boolean>();

  @ViewChild('jobinformation') jobinformation: OrderDetailJobinformationComponent;
  @ViewChild('orderdata') orderdata: OrderDetailOrderdataComponent;
  @ViewChild('candidates') candidates: OrderDetailCandidatesComponent;
  @ViewChild('participants') participants: OrderDetailParticipantsComponent;
  @ViewChild('documents') documents: OrderDetailDocumentsComponent;

  public submitting = false;
  public busy = false;
  public clearingInfo: string = null;

  public form: UntypedFormGroup = this.fb.group({});
  public items: Array<MenuItem> = [];

  public moveVisible = false;
  public cancelVisible = false;
  public finishVisible = false;
  public cancelForm: UntypedFormGroup = this.fb.group({
    reason: new UntypedFormControl('')
  });
  public optionsReasons: Array<SelectItem> = [];

  public selectedTemplate: TemplateModel = null;
  public selectedBusinessunit: BusinessunitModel = null;
  public inEdit = false;
  public viewModes = DetailModes;

  constructor(
    private api: ApiService,
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private transloco: TranslocoService,
    private orderhelper: OrderHelperService,
    private confirmation: ConfirmationService,
    private auth: AuthStateService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.optionsReasons = FormHelper.getSelectList(CANCELLATION_REASONS, null, this.transloco);
  }

  ngOnChanges(): void {
    if (this.order) {
      this.showNodatesInfo();
      this.initMenu();
    }
    if (this.mode === DetailModes.new) {
      this.onEdit();
    }
  }

  public onEdit(): void {
    if (this.auth.hasRight('admin.planning-orders.edit')) {
      this.inEdit = true;
      this.setEdit.emit('basedata');
    }
  }

  public onCancel(): void {
    this.ui.hideMessage();
    this.documents.initData();
    if (this.mode === DetailModes.edit) {
      this.inEdit = false;
    } else {
      this.cancelled.emit();
    }
    this.setEdit.emit(null);
  }

  public onSubmit(): void {
    if (this.auth.hasRight('admin.planning-orders.edit')) {
      let message = null;
      this.ui.hideMessage();
      const payload = this.getPayload();
      if (this.form.valid) {
        this.submitting = true;
        this.api.saveModel('orders', (this.isEditMode()) ? this.order.id : null, payload)
          .pipe(
            switchMap((result: any) => {
              this.order = new AcModel().deserialize(result.data);
              if (result.message) {
                message = result.message;
              }
              this.orderChange.emit(this.order);
              this.setClearingUrl.emit((this.order.clearing === true));
              if (this.auth.canSeeFiles()) {
                const uploadPayload = this.documents.getPayload();
                const allFiles = this.documents.getFiles();
                // Kandidaten-IDs als String-Array
                const candidateIds = (this.order.candidates || []).map((c: any) => String(c.id));
                // Alle Keys, die Requests bekommen sollen: 'AC' + alle Kandidaten
                const uploadKeys = ['AC', ...candidateIds];
                const uploadRequests = uploadKeys.map((key) => {
                  const payloadForKey = uploadPayload[key] || {};
                  const fileKeys = Object.keys(payloadForKey);
                  const files = allFiles.filter(f => fileKeys.includes(f.name));
                  if (key === 'AC') {
                    return this.api.uploadFiles(files, 'ac.file', this.order.id, payloadForKey, UPLOAD_MODE_MULTI);
                  } else {
                    return this.api.uploadFiles(files, 'candidate.file', Number(key), payloadForKey, UPLOAD_MODE_MULTI);
                  }
                });
                return forkJoin(uploadRequests);
              } else {
                return of([]);
              }
            })
          )
          .subscribe({
            next: (results: any) => {
              let merged = [];
              if (Array.isArray(results)) {
                results.forEach((res: any) => {
                  if (res && Array.isArray(res.data)) {
                    merged = merged.concat(res.data.map((resource: any) => new ResourceModel().deserialize(resource)));
                  }
                });
              }
              this.documents.initData(merged);
              this.ui.toastSuccess(this.transloco.translate((this.isEditMode()) ? 'common.messages.order_saved' : 'common.messages.order_created'));
              if (this.isEditMode()) {
                this.inEdit = false;
                this.setEdit.emit(null);
                this.showNodatesInfo();
                this.initMenu();
              } else {
                const mode = (this.order.clearing === true) ? 'clearings' : 'orders';
                this.router.navigate(['/planning/' + mode, this.order.id]);
              }
            },
            error: (error: any) => {
              this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
              FormHelper.setBackendErrors(this.form, error.fielderrors);
            },
          })
          .add(() => {
            this.submitting = false;
            if (message !== null) {
              this.participants.showSubmitMessages(message);
            }
          });
      } else {
        this.ui.showMessage('error', this.transloco.translate('backend.exceptions.error_on_form_please_correct'), this.transloco.translate('common.error'));
        FormHelper.markFormAsDirty(this.form);
      }
    }
  }

  public onTakeOrder(): void {
    if (this.auth.hasRight('admin.planning-orders.order')) {
      if (this.isEditMode() && this.order?.id && this.order.isOrderable()) {
        this.confirmation.confirm({
          message: this.transloco.translate('common.messages.really_order', { value: this.order?.name }),
          header: this.transloco.translate('common.really_order'),
          icon: 'pi pi-exclamation-triangle',
          accept: () => {
            this.orderAc();
          }
        });
      }
    }
  }

  public onReplaceOrder(order: AcModel) {
    this.orderChange.emit(order);
  }

  protected orderAc(force: boolean = false): void {
    this.submitting = true;
    this.api.orderAc(this.order.id, force).subscribe({
      next: (result: any) => {
        this.order = new AcModel().deserialize(result.data);
        this.orderChange.emit(this.order);
        this.ui.toastSuccess(this.transloco.translate('common.messages.ordered', { value: this.order.name }));
        this.initMenu();
      },
      error: (error: any) => {
        if (error.message?.missing || error.message?.nodates) {
          this.order.clearing_info = {
            missing: error.message?.missing || null,
            nodates: error.message?.nodates || null,
          };
          this.orderhelper.showNodatesInfo(this.order, true).then(() => {
            this.orderAc(true);
          });
        } else {
          this.ui.toastError(this.transloco.translate(error.message));
        }
      }
    }).add(() => {
      this.submitting = false;
    });
  }

  public onCancelOrder(): void {
    if (this.isEditMode() && this.order?.id && this.order?.isCancellable()) {
      if (this.order?.status === 'requested') {
        this.cancelForm.get('reason').clearValidators();
      } else {
        this.cancelForm.get('reason').setValidators([Validators.required]);
      }
      this.cancelForm.get('reason').updateValueAndValidity();
      this.cancelVisible = true;
    }
  }

  public cancelCancellation(): void {
    this.cancelVisible = false;
    this.cancelForm.get('reason').setValue('');
    this.cancelForm.get('reason').markAsPristine();
    this.cancelForm.get('reason').markAsUntouched();
}

  public sendCancellation(): void {
    if (this.auth.hasRight('admin.planning-orders.rescind')) {
      if (this.cancelForm.valid) {
        const reason = this.cancelForm.get('reason').value;
        this.busy = true;
        this.cancelForm.get('reason').disable();
        this.api.cancelAc(this.order.id, reason).subscribe({
          next: (result: any) => {
            this.order = new AcModel().deserialize(result.data);
            this.orderChange.emit(this.order);
            this.ui.toastSuccess(this.transloco.translate('common.messages.rescinded', { value: this.order.name }));
            this.initMenu();
          },
          error: (error: any) => {
            this.ui.toastError(this.transloco.translate(error.message));
          }
        }).add(() => {
          this.busy = false;
          this.cancelCancellation();
          this.cancelForm.get('reason').enable();
        });
      } else {
        FormHelper.markFormAsDirty(this.cancelForm);
      }
    }
  }

  public onShowFinishAc(): void {
    if (this.auth.hasRight('admin.planning-orders.finish') && this.order.finishable === true) {
      this.finishVisible = true;
    }
  }

  public onFinishOrder(order: AcModel) {
    this.orderChange.emit(order);
    this.finishVisible = false;
  }

  public onDeleteOrder(): void {
    if (this.auth.hasRight('admin.planning-orders.delete')) {
      this.confirmation.confirm({
        message: this.transloco.translate('order_detail.really_delete_order', { value: this.order.name }),
        header: this.transloco.translate('common.really_delete'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.deleteModel('orders', this.order.id).subscribe({
            next: () => {
              this.ui.toastSuccess(this.transloco.translate('common.messages.deleted', { value: this.order.name }), this.transloco.translate('common.deleted'));
              this.router.navigate([this.backRoute]);
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message));
            }
          });
        }
      });
    }
  }

  public sendInvitation(data: any): void {
    if (this.auth.hasRight('admin.planning-orders.edit') && data.role && data.id && data.name) {
      let type = null;
      switch (data.role) {
        case 'candidate': {
          type = 'candidate';
          break;
        }
        case 'moderator':
        case 'moderator_external': {
          type = 'moderator';
          break;
        }
        case 'observer':
        case 'observer_gem': {
          type = 'observer';
          break;
        }
        default: {
          type = 'user';
          break;
        }
      }
      if (type !== null) {
        const payload: any = {
          recipient: data.id,
          mode: data.mode,
          type,
        };
        const modeName = data.mode || 'invitation';
        this.confirmation.confirm({
          message: this.transloco.translate('freeorder.resend_' + modeName, { value: data.name }),
          header: this.transloco.translate('freeorder.' + modeName),
          icon: 'pi pi-exclamation-triangle',
          accept: () => {
            this.api.sendAcInvitations(this.order.id, payload).subscribe({
              next: () => {
                this.ui.toastSuccess(this.transloco.translate('freeorder.' + modeName +'_sended_to', { value: data.name }), this.transloco.translate('freeorder.' + modeName));
              },
              error: (error: any) => {
                this.ui.toastError(this.transloco.translate(error.message), this.transloco.translate('freeorder.' + modeName));
              }
            });
          }
        });
      }
    }
  }

  public templateChanged(event: TemplateModel | null): void {
    this.selectedTemplate = event;
  }

  public businessunitChanged(event: BusinessunitModel | null): void {
    this.selectedBusinessunit = event;
  }

  public onMoveAc(): void {
    this.moveVisible = true;
  }

  private showNodatesInfo(): void {
    this.clearingInfo = (this.order?.clearing_info !== null) ? this.orderhelper.getNodatesContent(this.order, false, true) : null;
  }

  private getPayload(): any {
    return {
      jobinformation: this.jobinformation.getPayload(),
      orderdata: this.orderdata.getPayload(),
      candidates: this.candidates.getPayload(),
      participants: this.participants.getPayload(),
    };
  }

  private isEditMode(): boolean {
    return this.mode === DetailModes.edit;
  }

  private initMenu(): void {
    this.items = [];
    if (this.order !== null) {
      if (this.auth.hasRight('admin.planning-orders.edit') && this.order.movable === true) {
        this.items.push({
          label: this.transloco.translate('common.labels.change_date_location'),
          icon: 'pi pi-calendar',
          command: () => {
            this.onMoveAc();
          }
        });
      }
      if (this.auth.hasRight('admin.planning-orders.finish') && this.order.finishable === true) {
        this.items.push({
          label: this.transloco.translate('common.labels.finish'),
          icon: 'pi pi-check',
          command: () => {
            this.onShowFinishAc();
          }
        });
      }
      if (this.auth.hasRight('admin.planning-orders.rescind') && this.order.isCancellable()) {
        this.items.push({
          label: this.transloco.translate('common.labels.rescind'),
          icon: 'pi pi-ban',
          command: () => {
            this.onCancelOrder();
          }
        });
      }
      if (this.auth.hasRight('admin.planning-orders.delete') && this.order.deletable === true) {
        this.items.push({
          label: this.transloco.translate('common.labels.delete'),
          icon: 'pi pi-trash',
          styleClass: 'danger-button',
          command: () => {
            this.onDeleteOrder();
          }
        });
      }
    }
  }

}
